import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAdmin } from '../hooks/useAdmin';
import { AdminDashboard } from '../features/admin/AdminDashboard';
import { apiaryService, hiveService, inspectionService, beebaseService } from '../services/firebase';
import { trackPageView } from '../utils/analytics';
import ApiariesPageWeatherCards from './ApiariesPageWeatherCards';
import HivesPage from './HivesPageEnhanced';
import EnhancedInspectionsPage from './EnhancedInspectionsPage';
import BeeBaseAlertsPage from './BeeBaseAlertsPage';
import ExportPage from './ExportPage';
import AccountManager from './AccountManager';
import AccountMergeComponent from './AccountMergeComponent';
import Header from './Header';
import DashboardHome from './DashboardHome';
import './Dashboard.css';
import './HamburgerNavigation.css';

type ActivePage = 'dashboard' | 'apiaries' | 'hives' | 'inspections' | 'alerts' | 'export' | 'admin';

interface DashboardStats {
  totalApiaries: number;
  totalHives: number;
  totalInspections: number;
  recentInspections: any[];
}

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { isAdmin } = useAdmin();
  const [activePage, setActivePage] = useState<ActivePage>('dashboard');
  const [alerts, setAlerts] = useState<any[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [showAccountManager, setShowAccountManager] = useState(false);
  const [showAccountMerge, setShowAccountMerge] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    totalApiaries: 0,
    totalHives: 0,
    totalInspections: 0,
    recentInspections: []
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [statsRetryCount, setStatsRetryCount] = useState(0);
  const MAX_STATS_RETRIES = 1;

  const loadDashboardStats = useCallback(async (currentRetryCount = statsRetryCount) => {
    if (!user) return;
    
    setLoadingStats(true);
    setAlertsLoading(true);
    try {
      const [apiaries, hives, recentInspections, activeAlerts] = await Promise.all([
        apiaryService.getUserApiaries(user.uid).catch(() => []),
        hiveService.getUserHives(user.uid).catch(() => []),
        inspectionService.getUserRecentInspections(user.uid, 5).catch(() => []),
        beebaseService.getAllActiveAlerts().catch(() => [])
      ]);
      
      // Enrich inspections with hive information
      const enrichedInspections = recentInspections.map(inspection => {
        const hive = hives.find(h => h.id === inspection.hive_id);
        const apiary = hive ? apiaries.find(a => a.id === hive.apiary_id) : null;
        return {
          ...inspection,
          hiveName: hive?.name || 'Unknown Hive',
          apiaryName: apiary?.name || 'Unknown Apiary'
        };
      });
      
      setAlerts(activeAlerts);
      setStats({
        totalApiaries: apiaries.length,
        totalHives: hives.length,
        totalInspections: recentInspections.length,
        recentInspections: enrichedInspections
      });
      
      // Check if this might be an account merge scenario
      // If the user has no data but is authenticated, they might need to merge accounts
      if (apiaries.length === 0 && hives.length === 0 && recentInspections.length === 0 && user.email) {
        setShowAccountMerge(true);
      } else {
        setShowAccountMerge(false);
      }
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      
      // If it's a permissions error during account merge, retry once
      if (error instanceof Error && error.message.includes('permissions') && currentRetryCount < MAX_STATS_RETRIES) {
        const nextRetryCount = currentRetryCount + 1;
        console.log(`Retrying dashboard stats after permissions error... (attempt ${nextRetryCount}/${MAX_STATS_RETRIES})`);
        setStatsRetryCount(nextRetryCount);
        setTimeout(() => {
          loadDashboardStats(nextRetryCount);
        }, 3000);
      } else {
        // Set default stats for new users or other errors
        console.warn('Dashboard stats loading failed after retries, showing empty state');
        setStatsRetryCount(0); // Reset for next time
        setStats({
          totalApiaries: 0,
          totalHives: 0,
          totalInspections: 0,
          recentInspections: []
        });
        setAlerts([]);
      }
    } finally {
      setLoadingStats(false);
      setAlertsLoading(false);
    }
  }, [user, statsRetryCount]);

  useEffect(() => {
    if (user && activePage === 'dashboard') {
      setStatsRetryCount(0); // Reset retry count for fresh load
      loadDashboardStats(0);
    }
  }, [user, activePage, loadDashboardStats]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handlePageChange = (page: ActivePage) => {
    setActivePage(page);
    setMobileMenuOpen(false); // Close mobile menu when navigating
    
    // Track page views
    trackPageView(page);
    
    // Refresh dashboard stats when returning to dashboard
    if (page === 'dashboard') {
      setTimeout(() => loadDashboardStats(), 100);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const renderActivePage = () => {
    switch (activePage) {
      case 'apiaries':
        return <ApiariesPageWeatherCards onNavigateToInspections={() => setActivePage('inspections')} />;
      case 'hives':
        return <HivesPage />;
      case 'inspections':
        return <EnhancedInspectionsPage />;
      case 'alerts':
        return <BeeBaseAlertsPage />;
      case 'admin':
        return <AdminDashboard />;
      case 'export':
        return <ExportPage />;
      default:
        return (
          <DashboardHome 
            stats={stats}
            loadingStats={loadingStats}
            alerts={alerts}
            alertsLoading={alertsLoading}
            onNavigate={handlePageChange}
          />
        );
    }
  };

  return (
    <div className="dashboard">
      <Header 
        onToggleMobileMenu={toggleMobileMenu}
        mobileMenuOpen={mobileMenuOpen}
        onShowAccountManager={() => setShowAccountManager(true)}
        activePage={activePage}
        onNavigate={handlePageChange}
      />

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Mobile Menu */}
      <nav className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-menu-header">
          <h3>🐝 Navigation</h3>
        </div>
        
        <div className="mobile-menu-content">
          {/* Main Navigation */}
          <div className="mobile-menu-section">
            <h4>Main</h4>
            
            <button 
              className={`mobile-menu-item ${activePage === 'dashboard' ? 'active' : ''}`}
              onClick={() => handlePageChange('dashboard')}
            >
              <i className="fas fa-tachometer-alt"></i>
              <span>Dashboard</span>
            </button>
            
            <button 
              className={`mobile-menu-item ${activePage === 'apiaries' ? 'active' : ''}`}
              onClick={() => handlePageChange('apiaries')}
            >
              <i className="fas fa-map-marker-alt"></i>
              <span>Apiaries</span>
            </button>
            
            <button 
              className={`mobile-menu-item ${activePage === 'hives' ? 'active' : ''}`}
              onClick={() => handlePageChange('hives')}
            >
              <i className="fas fa-home"></i>
              <span>Hives</span>
            </button>
            
            <button 
              className={`mobile-menu-item ${activePage === 'inspections' ? 'active' : ''}`}
              onClick={() => handlePageChange('inspections')}
            >
              <i className="fas fa-clipboard-check"></i>
              <span>Inspections</span>
            </button>
          </div>

          {/* Alerts & Data */}
          <div className="mobile-menu-section">
            <h4>Alerts & Data</h4>
            
            <button 
              className={`mobile-menu-item ${activePage === 'alerts' ? 'active' : ''}`}
              onClick={() => handlePageChange('alerts')}
            >
              <i className="fas fa-exclamation-triangle"></i>
              <span>BeeBase Alerts</span>
            </button>
            
            <button 
              className={`mobile-menu-item ${activePage === 'export' ? 'active' : ''}`}
              onClick={() => handlePageChange('export')}
            >
              <i className="fas fa-download"></i>
              <span>Export Data</span>
            </button>

            {isAdmin && (
              <button 
                className={`mobile-menu-item ${activePage === 'admin' ? 'active' : ''}`}
                onClick={() => handlePageChange('admin')}
              >
                <i className="fas fa-chart-line"></i>
                <span>Admin</span>
              </button>
            )}
          </div>

          {/* Account */}
          <div className="mobile-menu-section">
            <h4>Account</h4>
            
            <button 
              className="mobile-menu-item"
              onClick={() => {
                setShowAccountManager(true);
                closeMobileMenu();
              }}
            >
              <i className="fas fa-user"></i>
              <span>My Account</span>
            </button>
          </div>
        </div>

        <div className="mobile-menu-footer">
          <button 
            className="mobile-menu-item"
            onClick={handleLogout}
          >
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="dashboard-content">
        {/* Account Merge Component - Show when user has no data but is authenticated */}
        {showAccountMerge && activePage === 'dashboard' && (
          <AccountMergeComponent 
            onSuccess={() => {
              setShowAccountMerge(false);
              // Refresh the dashboard after successful merge
              setTimeout(() => loadDashboardStats(), 1000);
            }}
            onError={(error) => {
              console.error('Account merge error:', error);
              // Keep the component visible so user can try again
            }}
          />
        )}
        
        {renderActivePage()}
      </main>

      {/* Account Manager Modal */}
      <AccountManager 
        isOpen={showAccountManager}
        onClose={() => setShowAccountManager(false)}
      />
    </div>
  );
};

export default Dashboard;